import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { IAddress } from '../../../../extViewmodels';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { Button } from '../../../components-v2/Button';
import { LoadingSpinner } from '../../LoadingSpinner';
import { asModalComponent } from '../../Modal';
import { Input } from '../../../components-v2/Input';
import { CountrySelector } from '../../ValueSelectors/CountrySelector';
import { INameValue } from '../../ValueSelectors/ValueSelector';
import { styleSheet } from './styles';

export type INameExtendedAddress = IAddress & { firstName?: string; lastName?: string };

interface IProps extends IModalContext<any> {
	initAddress?: INameExtendedAddress;
	isLoading?: boolean;
	onClickToSave: (updatedAddress: INameExtendedAddress) => void;
	withNames?: boolean;
	saveButtonLabel?: string;
	onCancel?: () => void;
}

export const UpdateAddress: React.FC<IProps> = ({
	initAddress,
	isLoading = false,
	onClickToSave,
	parentModal,
	withNames = false,
	saveButtonLabel = 'Save',
	onCancel,
}) => {
	const [address, setAddress] = useState<INameExtendedAddress>(initAddress);
	const handleClickToSave = () => {
		onClickToSave(address);
	};
	const handleFieldChange = (field: keyof INameExtendedAddress) => (ev: React.ChangeEvent<HTMLInputElement>) => {
		setAddress({
			...address,
			[field]: ev.target.value,
		});
	};
	const onCountryChanged = (country: INameValue) => {
		setAddress({
			...address,

			country: country.value !== 'None' ? country.value : null,
		});
	};
	return (
		<>
			{isLoading ? (
				<div className={css(styleSheet.modalLoadingOverlay)}>
					<LoadingSpinner type='large' />
				</div>
			) : null}
			{!onCancel ? (
				<div className={css(styleSheet.modalHeader)}>
					<h1 className={css(styleSheet.modalHeaderName)}>Add Address</h1>
				</div>
			) : null}
			{withNames ? (
				<div className={css(styleSheet.addresslineGroup)}>
					<div className={css(styleSheet.addresslineCol)}>
						<label className={css(styleSheet.label)} htmlFor='address-modal-firstName-input'>
							First Name
						</label>
						<Input
							name='firstName'
							id='address-modal-firstName-input'
							onChange={handleFieldChange('firstName')}
							type='text'
							value={address?.firstName}
						/>
					</div>
					<div className={css(styleSheet.addresslineCol)}>
						<label className={css(styleSheet.label)} htmlFor='address-modal-lastName-input'>
							Last Name
						</label>
						<Input
							name='lastName'
							id='address-modal-lastName-input'
							onChange={handleFieldChange('lastName')}
							type='text'
							value={address?.lastName}
						/>
					</div>
				</div>
			) : null}

			<label className={css(styleSheet.label)} htmlFor='address-modal-address1-input'>
				Address Line 1
			</label>
			<Input
				name='address1'
				id='address-modal-address1-input'
				onChange={handleFieldChange('address1')}
				type='text'
				placeholder='e.g. 123 Main St.'
				value={address?.address1}
			/>
			<label className={css(styleSheet.label)} htmlFor='address-modal-address2-input'>
				Address Line 2
			</label>
			<Input
				name='address2'
				id='address-modal-address2-input'
				onChange={handleFieldChange('address2')}
				type='text'
				placeholder='e.g. Apt. 4B'
				value={address?.address2}
			/>
			<div data-testid='address-modal-addresslineGroup' className={`${css(styleSheet.addresslineGroup)}`}>
				<div className={css(styleSheet.addresslineCol)}>
					<label className={css(styleSheet.label)} htmlFor='address-modal-city-input'>
						City
					</label>
					<Input
						name='city'
						id='address-modal-city-input'
						onChange={handleFieldChange('city')}
						type='text'
						value={address?.city}
					/>
				</div>
				<div className={css(styleSheet.addresslineCol)}>
					<label className={css(styleSheet.label)} htmlFor='address-modal-state-input'>
						State/Province
					</label>
					<Input
						name='stateProvince'
						id='address-modal-state-input'
						onChange={handleFieldChange('stateProvince')}
						type='text'
						value={address?.stateProvince}
					/>
				</div>
				<div className={css(styleSheet.addresslineCol)}>
					<label className={css(styleSheet.label)} htmlFor='address-modal-postalCode-input'>
						Zip/Postal Code
					</label>
					<Input
						name='postalCode'
						id='address-modal-postalCode-input'
						onChange={handleFieldChange('postalCode')}
						type='text'
						value={address?.postalCode}
					/>
				</div>
			</div>
			<label className={css(styleSheet.label)} htmlFor='address-modal-country-input'>
				Country
			</label>
			<CountrySelector onSelectionChanged={onCountryChanged} selectedValue={address?.country} />
			<div className={css(styleSheet.modalFooter)}>
				<Button onClick={handleClickToSave}>{saveButtonLabel}</Button>
				<Button variant='secondary' onClick={onCancel ?? parentModal.onRequestClose}>
					Cancel
				</Button>
			</div>
		</>
	);
};

const UpdateAddressModalContainer = (props: IProps) => {
	return (
		<div className={css(styleSheet.modalContainer)}>
			<UpdateAddress {...props} />
		</div>
	);
};

const UpdateAddressModalWithContext = inject(ModalChildComponentContextKey)(UpdateAddressModalContainer);

export const UpdateAddressModal = asModalComponent(UpdateAddressModalWithContext, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
